
import { useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function SeaPointPage() {
  useEffect(() => {
    document.title = 'Sea Point Painters Cape Town | Luxury Coastal Painting | Atlantic Seaboard Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Sea Point, Cape Town. Expert luxury coastal painting, Atlantic Seaboard properties, and marine-grade protection. Premium painting contractors.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Sea Point painters, luxury coastal painting, Atlantic Seaboard Cape Town');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/sea-point`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Sea Point Painters Cape Town",
      "description": "Professional painting services in Sea Point, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/sea-point`,
      "areaServed": {
        "@type": "Place",
        "name": "Sea Point, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.9248,
          "longitude": 18.3917
        },
        "geoRadius": "8000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Sea Point Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/36c74ba1d3fc1efc778c98d20dc28175.jpg"
            alt="Professional painting services in Sea Point, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Premium <span className="text-yellow-400">Sea Point Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Luxury painting services for Sea Point's prestigious Atlantic Seaboard properties. Marine-grade protection and premium finishes for discerning coastal property owners.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Luxury Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Luxury Coastal Painting Services in Sea Point
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Premium painting solutions for Sea Point's exclusive Atlantic Seaboard properties, from luxury apartments to commercial establishments, with specialized marine protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-4-line',
                title: 'Luxury High-Rise Apartments',
                description: 'Specialized painting for luxury apartment buildings and penthouses with ocean views, using premium marine-grade materials.',
                features: ['Penthouse painting', 'High-rise buildings', 'Ocean view properties', 'Premium finishes']
              },
              {
                icon: 'ri-store-3-line',
                title: 'Commercial & Hospitality',
                description: 'Professional painting for restaurants, hotels, and retail spaces along the Sea Point promenade and commercial areas.',
                features: ['Restaurants & cafes', 'Hotels & lodges', 'Retail spaces', 'Promenade businesses']
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Marine Protection Systems',
                description: 'Advanced marine-grade coatings and protection systems designed for harsh Atlantic coastal conditions.',
                features: ['Marine-grade coatings', 'Salt air protection', 'UV resistance', 'Storm protection']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Sea Point Properties Choose Our Atlantic Seaboard Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Atlantic Seaboard Specialization',
                    description: 'Extensive experience with Sea Point\'s unique coastal challenges including salt air, ocean spray, and high-rise access.'
                  },
                  {
                    title: 'Luxury Standards',
                    description: 'Premium materials and meticulous attention to detail meeting the highest expectations of discerning Sea Point clients.'
                  },
                  {
                    title: 'High-Rise Expertise',
                    description: 'Specialized equipment and techniques for safely painting high-rise buildings and accessing challenging coastal locations.'
                  },
                  {
                    title: 'Hospitality Experience',
                    description: 'Understanding of tourism and hospitality needs with flexible scheduling to minimize disruption to business operations.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Sea%20Point%20luxury%20high-rise%20building%2C%20Atlantic%20Seaboard%20coastal%20painting%2C%20marine-grade%20equipment%20and%20materials%2C%20ocean%20view%20property%20maintenance%20Cape%20Town&width=600&height=700&seq=sea-point-team-work&orientation=portrait"
                alt="Luxury coastal painting in Sea Point"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Sea Point Luxury Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest luxury painting projects in Sea Point, showcasing our expertise in Atlantic Seaboard properties and marine protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Luxury Penthouse Complex',
                location: 'Sea Point Promenade',
                description: 'Complete exterior renovation of a 20-story luxury apartment complex with panoramic ocean views.',
                image: 'https://readdy.ai/api/search-image?query=Luxury%20penthouse%20complex%20painting%20in%20Sea%20Point%20Promenade%20Cape%20Town%2C%20high-rise%20apartment%20building%20with%20ocean%20views%2C%20premium%20coastal%20painting%2C%20Atlantic%20Seaboard%20architecture&width=400&height=300&seq=sea-point-project-1&orientation=landscape'
              },
              {
                title: 'Boutique Oceanfront Hotel',
                location: 'Sea Point Central',
                description: 'Professional painting renovation for a prestigious boutique hotel including guest rooms and public areas.',
                image: 'https://readdy.ai/api/search-image?query=Boutique%20oceanfront%20hotel%20painting%20in%20Sea%20Point%20Central%2C%20luxury%20hospitality%20property%20with%20Atlantic%20Ocean%20views%2C%20premium%20hotel%20painting%2C%20coastal%20tourism%20facility%20Cape%20Town&width=400&height=300&seq=sea-point-project-2&orientation=landscape'
              },
              {
                title: 'Promenade Restaurant Complex',
                location: 'Sea Point Beachfront',
                description: 'Comprehensive painting of a popular beachfront restaurant and entertainment complex.',
                image: 'https://readdy.ai/api/search-image?query=Promenade%20restaurant%20complex%20painting%20in%20Sea%20Point%20beachfront%2C%20oceanfront%20dining%20establishment%2C%20commercial%20coastal%20property%20painting%2C%20Atlantic%20Seaboard%20restaurant%20Cape%20Town&width=400&height=300&seq=sea-point-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Sea Point Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied luxury property owners and businesses in Sea Point
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Victoria Sterling',
                location: 'Sea Point Promenade',
                rating: 5,
                text: 'Exceptional work on our penthouse complex. The marine-grade coatings have protected our building beautifully against the Atlantic conditions. Truly premium service.'
              },
              {
                name: 'Marcus Thompson',
                location: 'Sea Point Central',
                rating: 5,
                text: 'Outstanding painting service for our boutique hotel. They understood our hospitality needs and delivered flawless results with minimal guest disruption.'
              },
              {
                name: 'Isabella Rodriguez',
                location: 'Sea Point Beachfront',
                rating: 5,
                text: 'Professional and reliable service for our restaurant complex. The quality of work and attention to detail exceeded our expectations. Highly recommended.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Elevate Your Sea Point Atlantic Seaboard Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for premium coastal painting consultation and luxury marine protection. Experience the finest in Atlantic Seaboard painting services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
